import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/* iOS Safari only applies :active on tap when a touchstart listener exists (the size and quantity press states
   in natysports.css use :active). */
document.addEventListener('touchstart', () => {}, { passive: true });

/* Touch press feedback (natysports.css, "Touch press feedback"): .ns-pressed on the touched control, held at least
   PRESS_MIN_MS so a quick tap is visible. A touch that turns into a scroll (pointercancel) clears it at once. */
const PRESS_SELECTOR = [
  '.header-actions__action',
  '.menu-drawer__close-button',
  '.header__icon--menu',
  '.menu-drawer__menu-item--mainlist',
  '.menu-drawer__menu-item--parent',
  '.drawer-localization__button',
  '.button',
  '.product-card',
  '.resource-card',
].join(', ');
const PRESS_MIN_MS = 250;

document.addEventListener(
  'pointerdown',
  (event) => {
    if (event.pointerType !== 'touch') return;
    // Walk the real tap path: a tap inside a component (e.g. the close button in <shopify-account>'s sign-in sheet)
    // reports the component itself as event.target, which would light up the account icon. Stop at a dialog that
    // comes before any control, since the tap was inside that dialog, not on the control around it.
    let control = null;
    for (const node of event.composedPath()) {
      if (!(node instanceof Element)) continue;
      if (node.matches(PRESS_SELECTOR)) {
        control = node;
        break;
      }
      if (node.matches('dialog')) return;
    }
    if (!control) return;
    const start = performance.now();
    control.classList.add('ns-pressed');
    const release = (end) => {
      if (end.pointerId !== event.pointerId) return;
      document.removeEventListener('pointerup', release);
      document.removeEventListener('pointercancel', release);
      const wait = end.type === 'pointercancel' ? 0 : Math.max(0, PRESS_MIN_MS - (performance.now() - start));
      setTimeout(() => control.classList.remove('ns-pressed'), wait);
    };
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', release);
  },
  { passive: true }
);

/* Quick-add size bar (snippets/ns-quick-size.liquid): show which size is being added, then confirm in the label.
   While an add is on its way the bar is busy (data-busy), so a double click adds one item, not two. */
const CONFIRM_MS = 2000;
const ERROR_MS = 4000;

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.ns-quick-size__size') : null;
  if (!button || button.disabled) return;
  const root = button.closest('.ns-quick-size');
  if (root instanceof HTMLElement && root.dataset.busy) {
    event.preventDefault();
    return;
  }
  root?.querySelectorAll('.is-adding').forEach((b) => b.classList.remove('is-adding'));
  button.classList.add('is-adding');
});

/* Product price: "or 4 interest-free payments of $14.00 with Shop Pay" (amount in bold) under the price,
   instead of Shopify's installments banner. Uses the eligibility and amounts Shopify puts on <shopify-payment-terms>, so it only shows when
   Shop Pay Installments really applies. The price block re-renders on variant change, so this re-runs. */
function renderInstallments() {
  document.querySelectorAll('.product-details product-price').forEach((price) => {
    const terms = price.querySelector('shopify-payment-terms');
    let text = '';
    let amount = '';
    try {
      const meta = JSON.parse(terms?.getAttribute('shopify-meta') ?? '{}');
      const variant = meta.variants?.find((v) => String(v.id) === terms?.getAttribute('variant-id'));
      if (variant?.eligible && variant.price_per_term && variant.number_of_payment_terms) {
        amount = variant.price_per_term;
        text = `or ${variant.number_of_payment_terms} interest-free payments of ${amount} with Shop\u00a0Pay`;
      }
    } catch {
      text = '';
    }

    let line = price.querySelector('.ns-installments');
    if (!text) {
      line?.remove();
      return;
    }
    if (line?.textContent === text) return;
    if (!line) {
      line = document.createElement('span');
      line.className = 'ns-installments';
      price.querySelector('[ref="priceContainer"]')?.after(line);
    }
    const [before, after] = text.split(amount);
    line.replaceChildren(before, Object.assign(document.createElement('strong'), { textContent: amount }), after);
  });

  /* Cart drawer and /cart: the same wording, kept to one line. Shopify's banner wraps its "shop ⓘ" onto a second
     line on phones. "interest-free " sits in its own span, which natysports.css hides when the space is too narrow. */
  document.querySelectorAll('.cart-totals__installments').forEach((slot) => {
    const terms = slot.querySelector('shopify-payment-terms');
    let text = '';
    let count = 0;
    let amount = '';
    try {
      const meta = JSON.parse(terms?.getAttribute('shopify-meta') ?? '{}');
      if (meta.eligible && meta.price_per_term && meta.number_of_payment_terms) {
        count = meta.number_of_payment_terms;
        amount = meta.price_per_term;
        text = `or ${count} interest-free payments of ${amount} with Shop\u00a0Pay`;
      }
    } catch {
      text = '';
    }

    let line = slot.querySelector('.ns-installments');
    slot.classList.toggle('ns-has-installments', Boolean(text));
    if (!text) {
      line?.remove();
      return;
    }
    // Compare a stored copy: textContent differs from text (hidden span, plain space), and rewriting the line
    // every time would retrigger the MutationObserver below on every frame.
    if (line?.dataset.text === text) return;
    if (!line) {
      line = document.createElement('span');
      line.className = 'ns-installments';
      slot.append(line);
    }
    line.dataset.text = text;
    line.replaceChildren(
      `or ${count} `,
      Object.assign(document.createElement('span'), { className: 'ns-installments__extra', textContent: 'interest-free ' }),
      'payments of ',
      Object.assign(document.createElement('strong'), { textContent: amount }),
      ' with Shop Pay'
    );
  });
}

/* Cart drawer and /cart express buttons (snippets/cart-summary.liquid): only two, half-width in one row. Shopify renders
   every wallet enabled in the admin (Shop Pay, Amazon Pay, Apple Pay, PayPal, Google Pay) and reads the list
   only when the element connects, so we swap in a copy whose wallet-configs holds Shop Pay plus Apple Pay on
   Apple devices, or PayPal elsewhere (orders Sep-Oct 2026: Shop Pay 48%, PayPal 18%, Apple Pay 15%,
   Google Pay under 1%). The other wallets are still offered at checkout. */
function trimExpressButtons() {
  const applePay = Boolean(window.ApplePaySession?.canMakePayments?.());
  const wanted = ['shop_pay', applePay ? 'apple_pay' : 'paypal'];

  document
    .querySelectorAll('.ns-express shopify-accelerated-checkout-cart:not([data-ns-trimmed])')
    .forEach((element) => {
      let configs;
      try {
        configs = JSON.parse(element.getAttribute('wallet-configs') ?? '[]');
      } catch {
        return;
      }
      const kept = configs.filter((config) => wanted.includes(config.name));
      if (!kept.length) return;

      const copy = document.createElement('shopify-accelerated-checkout-cart');
      for (const { name, value } of element.attributes) copy.setAttribute(name, value);
      copy.setAttribute('wallet-configs', JSON.stringify(kept));
      copy.setAttribute('data-ns-trimmed', '');
      element.replaceWith(copy);
    });
}

/* Product page Add to bag (phones, styles in natysports.css): the total for the chosen quantity after the label,
   "Add to bag | $198.00". Reads the shown price, so it follows variant changes, sale prices and the visitor's
   currency, and writes the total in the same format. */
function parsePrice(text) {
  const raw = text.match(/\d(?:[\d.,\s]*\d)?/)?.[0];
  if (!raw) return null;
  const decimal = /[.,]\d{2}$/.test(raw) ? raw.at(-3) : '';
  const thousands = raw.match(/\d([.,\s])\d{3}(?!\d)/)?.[1] ?? (decimal === ',' ? '.' : ',');
  const digits = Number(raw.replace(/\D/g, ''));
  return { text, raw, decimal, thousands, cents: decimal ? digits : digits * 100 };
}

function formatLike(price, cents) {
  const whole = Math.floor(cents / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, price.thousands);
  const amount = price.decimal ? `${whole}${price.decimal}${String(cents % 100).padStart(2, '0')}` : whole;
  return price.text.replace(price.raw, amount);
}

function renderButtonTotal() {
  document.querySelectorAll('.product-information .product-details').forEach((details) => {
    if (details.closest('dialog')) return;
    const button = details.querySelector('add-to-cart-component .add-to-cart-button');
    if (!button) return;
    const priceText = details.querySelector('product-price .price bdi')?.textContent.trim() ?? '';
    const price = priceText.includes(' - ') ? null : parsePrice(priceText);
    const quantity = Math.max(1, Number(details.querySelector('.quantity-selector input')?.value) || 1);
    const text = price && !button.disabled ? formatLike(price, price.cents * quantity) : '';

    let span = button.querySelector('.ns-atc-price');
    if (!text) {
      span?.remove();
      return;
    }
    if (span?.textContent === text) return;
    if (!span) {
      span = document.createElement('span');
      span.className = 'ns-atc-price';
      button.querySelector('.add-to-cart__added')?.before(span);
    }
    span.textContent = text;
  });
}

document.addEventListener('quantity-selector:update', () => requestAnimationFrame(renderButtonTotal));
document.addEventListener('input', (event) => {
  if (event.target instanceof Element && event.target.closest('.product-details .quantity-selector')) {
    renderButtonTotal();
  }
});

/* Phone buy bar (styles in natysports.css): quantity and Add to bag pin to the bottom of the screen only once their
   row has scrolled up under the header, so the shopper has passed the sizes first. The bar names the chosen size. The state lives on body, not on the form, because Horizon re-renders the form on variant
   changes. */
const phone = window.matchMedia('(max-width: 749px)');
let buyRowSpace = 0;

function buyBarSizeLabel() {
  let label = document.querySelector('.ns-buy-bar-size');
  if (!label) {
    label = document.createElement('div');
    label.className = 'ns-buy-bar-size';
    label.hidden = true;
    document.body.append(label);
  }
  return label;
}

function renderBuyBar() {
  const details = [...document.querySelectorAll('.product-information .product-details')].find(
    (element) => !element.closest('dialog')
  );
  const buttons = details?.querySelector('.product-form-buttons');
  const row = buttons?.querySelector(':scope > span:has(add-to-cart-component)');
  const body = document.body;
  if (!row || !phone.matches) {
    body.classList.remove('ns-buy-bar-on');
    return;
  }

  const top = buttons.getBoundingClientRect().top;
  if (!body.classList.contains('ns-buy-bar-on')) {
    const next = [...buttons.children].slice([...buttons.children].indexOf(row) + 1).find((element) => element.getClientRects().length);
    buyRowSpace = (next ? next.getBoundingClientRect().top : row.getBoundingClientRect().bottom) - top;
  }
  const headerBottom = Math.max(0, document.querySelector('#header-component')?.getBoundingClientRect().bottom ?? 0);
  const on = top + buyRowSpace - 8 < headerBottom;
  body.classList.toggle('ns-buy-bar-on', on);
  body.style.setProperty('--ns-buy-row-space', `${buyRowSpace}px`);

  const size = [...details.querySelectorAll('.ns-option-value')].find((value) =>
    /^\s*size\b/i.test(value.parentElement?.textContent ?? '')
  );
  const label = buyBarSizeLabel();
  const text = size?.textContent.trim() ?? '';
  label.hidden = !text;
  if (text && label.dataset.size !== text) {
    label.dataset.size = text;
    label.replaceChildren('Size:', Object.assign(document.createElement('strong'), { textContent: text }));
  }
}

let buyBarFrame = 0;
const queueBuyBar = () => {
  cancelAnimationFrame(buyBarFrame);
  buyBarFrame = requestAnimationFrame(renderBuyBar);
};
window.addEventListener('scroll', queueBuyBar, { passive: true });
window.addEventListener('resize', queueBuyBar);

function onPageChange() {
  renderInstallments();
  trimExpressButtons();
  renderButtonTotal();
  renderBuyBar();
}

let installmentsFrame = 0;
onPageChange();
new MutationObserver(() => {
  cancelAnimationFrame(installmentsFrame);
  installmentsFrame = requestAnimationFrame(onPageChange);
}).observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['variant-id', 'shopify-meta', 'data-ns-trimmed'],
});

/* Desktop photo zoom (styles in natysports.css): clicking the photo magnifies it and it follows the mouse;
   clicking again or scrolling to another photo zooms back out. Clicks beside the photo still close the dialog.
   Listens on window in the capture phase so it runs before Horizon's on:click="/close" on the slide. */
const desktopZoom = window.matchMedia('(min-width: 750px) and (hover: hover)');

const unmagnify = (dialog) =>
  dialog?.querySelectorAll('.ns-magnified').forEach((slide) => slide.classList.remove('ns-magnified'));

/* Pointer position across the whole slide (which never scales), so sweeping the mouse edge to edge pans
   the whole photo. */
const setZoomOrigin = (slide, event) => {
  const rect = slide.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  slide.style.setProperty('--ns-zoom-x', `${Math.min(100, Math.max(0, x))}%`);
  slide.style.setProperty('--ns-zoom-y', `${Math.min(100, Math.max(0, y))}%`);
};

window.addEventListener(
  'click',
  (event) => {
    if (!desktopZoom.matches || !(event.target instanceof Element)) return;
    const image = event.target.closest('.dialog-zoomed-gallery .product-media__image');
    const slide = image?.closest('.dialog-zoomed-gallery > li');
    if (!slide) return;

    event.stopPropagation();
    if (slide.classList.contains('ns-magnified')) {
      slide.classList.remove('ns-magnified');
    } else {
      setZoomOrigin(slide, event);
      slide.classList.add('ns-magnified');
    }
  },
  { capture: true }
);

document.addEventListener('mousemove', (event) => {
  if (!(event.target instanceof Element)) return;
  const slide = event.target.closest('.dialog-zoomed-gallery > li.ns-magnified');
  if (slide) setZoomOrigin(slide, event);
});

document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches('.dialog-zoomed-gallery__dialog')) {
      unmagnify(event.target);
    }
  },
  { capture: true }
);

document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof Element && event.target.matches('.dialog-zoomed-gallery__dialog')) {
      unmagnify(event.target);
    }
  },
  { capture: true }
);

/* Cart "removed, Undo" message (styles in natysports.css). The trash button removes the line as usual;
   the message offers the exact line back (variant, quantity, line properties, subscription plan). A snapshot
   of /cart.js is kept so the line's details are known at the moment the trash button is clicked. */
const UNDO_MS = 6000;
const UNDO_KEYBOARD_MS = 10000; // removed with the keyboard: Undo gets focus, so allow time to use it
const cartRoot = window.Shopify?.routes?.root ?? '/';
let cartSnapshot = null;

const refreshCartSnapshot = () =>
  fetch(`${cartRoot}cart.js`)
    .then((response) => response.json())
    .then((cart) => (cartSnapshot = cart))
    .catch(() => {});

refreshCartSnapshot();
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  Promise.resolve(event.promise).catch(() => {}).then(refreshCartSnapshot);
});

let undoToast = null;
let undoTimer = 0;

const dismissUndo = () => {
  clearTimeout(undoTimer);
  const toast = undoToast;
  undoToast = null;
  // The cart slides back up while the message fades out.
  document.getElementById('cart-drawer')?.classList.remove('ns-undo-open');
  document.body.classList.remove('ns-undo-open');
  if (!toast) return;
  toast.dispatchEvent(new Event('ns-undo-removed'));
  // Focus was on Undo: put it on the next thing in the cart instead of dropping it to the top of the page.
  if (toast.contains(document.activeElement)) {
    const scope = toast.closest('dialog') ?? document;
    const next = scope.querySelector(
      '.cart-items__remove, .cart__checkout-button:not([disabled]), .ns-continue-link, .ns-empty-cart__link, .button'
    );
    if (next instanceof HTMLElement) next.focus({ preventScroll: true });
  }
  toast.classList.add('is-leaving');
  setTimeout(() => toast.remove(), 200);
};

async function restoreLine(line, toast) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((component) => component instanceof HTMLElement && component.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();

  // Dispatched from the message (outside the cart items), so the cart treats it like any other add.
  toast.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(line.variant_id), quantity: line.quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const item = { id: line.variant_id, quantity: line.quantity, properties: line.properties ?? {} };
    if (line.selling_plan_allocation) item.selling_plan = line.selling_plan_allocation.selling_plan.id;

    const response = await fetch(`${cartRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: sectionIds.join(','), sections_url: window.location.pathname }),
    }).then((r) => r.json());
    if (response.status) throw new Error(response.description || response.message);

    const cart = await fetch(`${cartRoot}cart.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: response.sections, source: 'ns-cart-undo', didError: false },
    });
    dismissUndo();
  } catch (error) {
    deferred.reject(error);
    const text = toast.querySelector('.ns-undo-toast__text');
    if (text) text.textContent = "Couldn't restore it. Try adding it again.";
    toast.querySelector('.ns-undo-toast__button')?.remove();
    clearTimeout(undoTimer);
    undoTimer = setTimeout(dismissUndo, UNDO_MS);
  }
}

/* Screen readers: one empty live region per place (inside the drawer's modal dialog, or on the page), filled a
   moment after the message appears so it is announced. A region inserted already filled often is not. */
function announce(container, message) {
  let live = container.querySelector(':scope > .ns-undo-live');
  if (!live) {
    live = document.createElement('div');
    live.className = 'ns-undo-live visually-hidden';
    live.setAttribute('aria-live', 'polite');
    container.append(live);
  }
  live.textContent = '';
  setTimeout(() => (live.textContent = message), 100);
}

function showUndo(line, title, anchor, { keyboard = false } = {}) {
  dismissUndo();

  const toast = document.createElement('div');
  toast.className = 'ns-undo-toast';

  const text = document.createElement('p');
  text.className = 'ns-undo-toast__text';
  const name = document.createElement('strong');
  name.textContent = title;
  text.append(name, ' has been removed.');

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ns-undo-toast__button';
  button.textContent = 'Undo';
  button.setAttribute('aria-label', `Undo, put ${title} back in your cart`);
  button.addEventListener('click', () => {
    button.disabled = true;
    paused = true;
    clearTimeout(undoTimer);
    restoreLine(line, toast);
  });

  toast.append(text, button);

  // Stays up while the pointer is on it or the shopper tabs into it; the time restarts when they leave.
  const ms = keyboard ? UNDO_KEYBOARD_MS : UNDO_MS;
  let paused = false;
  // Only focus the shopper moved here pauses; the Undo button gets focus on its own after a remove.
  let focusPaused = false;
  const pause = () => {
    paused = true;
    clearTimeout(undoTimer);
  };
  const resume = () => {
    if (!paused || button.disabled || toast.matches(':hover') || (focusPaused && toast.contains(document.activeElement))) return;
    paused = false;
    clearTimeout(undoTimer);
    undoTimer = setTimeout(dismissUndo, ms);
  };
  toast.addEventListener('pointerenter', pause);
  toast.addEventListener('pointerleave', resume);
  toast.addEventListener('focusin', () => {
    if (movingFocus) return;
    focusPaused = true;
    pause();
  });
  toast.addEventListener('focusout', () =>
    setTimeout(() => {
      if (!toast.contains(document.activeElement)) focusPaused = false;
      resume();
    })
  );

  const dialog = anchor.closest('#cart-drawer dialog');
  const drawer = dialog?.closest('#cart-drawer');
  undoToast = toast;

  if (dialog && drawer instanceof HTMLElement) {
    // Inside the drawer's modal dialog so it shows (and can be clicked) above it. The message gets its own space
    // under the heading: the cart slides down to make room instead of being covered. The state lives on
    // #cart-drawer, whose attributes cart re-renders keep.
    dialog.append(toast);
    const header = dialog.querySelector('.theme-drawer__header');
    const top = header ? header.getBoundingClientRect().bottom - dialog.getBoundingClientRect().top : 76;
    drawer.style.setProperty('--ns-undo-top', `${Math.round(top)}px`);
    drawer.style.setProperty('--ns-undo-space', `${toast.offsetHeight + 12}px`);
    drawer.classList.add('ns-undo-open');
  } else {
    // /cart: the same place as in the drawer, under the "Cart" heading. The section re-renders after the removal,
    // so the message lives on <body>, is laid over the space the items move down to make (state on <body> too),
    // and follows the heading if the layout changes.
    toast.classList.add('ns-undo-toast--page');
    document.body.append(toast);
    const place = () => {
      const heading = document.querySelector('.cart-page__title');
      const items = document.querySelector('.cart-page__items') ?? heading;
      if (!heading || !items) return;
      const box = items.getBoundingClientRect();
      toast.style.top = `${Math.round(heading.getBoundingClientRect().bottom + window.scrollY + 8)}px`;
      toast.style.left = `${Math.round(box.left + window.scrollX)}px`;
      toast.style.width = `${Math.round(box.width)}px`;
      document.body.style.setProperty('--ns-undo-space', `${toast.offsetHeight + 16}px`);
    };
    place();
    document.body.classList.add('ns-undo-open');
    const observer = new ResizeObserver(place);
    observer.observe(document.documentElement);
    toast.addEventListener('ns-undo-removed', () => observer.disconnect());
  }

  announce(dialog ?? document.body, `${title} has been removed. Press Undo to put it back.`);

  // The trash button that had focus is gone with its row, so hand focus to Undo rather than losing it.
  if (keyboard || document.activeElement === document.body || !document.activeElement?.isConnected || anchor.contains(document.activeElement)) {
    movingFocus = true;
    button.focus({ preventScroll: true });
    movingFocus = false;
  }
  undoTimer = setTimeout(dismissUndo, ms);
}

let movingFocus = false;

/* The line to offer back. Normally from the /cart.js snapshot; if that is missing or behind (removing right after
   adding), from the row itself: its variant and the quantity it shows. */
function lineFromRow(row) {
  const fromSnapshot = cartSnapshot?.items?.find((item) => item.key === row.dataset.key);
  const input = row.querySelector('input[name="updates[]"]');
  const shownQuantity = Number(input?.getAttribute('value'));
  if (fromSnapshot) {
    return shownQuantity > 0 ? { ...fromSnapshot, quantity: shownQuantity } : fromSnapshot;
  }
  const variantId = Number(
    row.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
      new URL(row.querySelector('a.cart-items__title, a[href*="variant="]')?.getAttribute('href') ?? '/', location.origin).searchParams.get('variant')
  );
  if (!variantId) return null;
  return { key: row.dataset.key, variant_id: variantId, quantity: shownQuantity > 0 ? shownQuantity : 1, properties: {} };
}

function offerUndo(row, anchor, keyboard) {
  const line = lineFromRow(row);
  if (!line) return;
  const title = row.querySelector('.cart-items__title')?.textContent.trim() || line.product_title || 'The item';
  showUndo(line, title, anchor, { keyboard });
}

window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.cart-items__remove') : null;
    const row = button?.closest('.cart-items__table-row');
    if (!button || !row) return;
    // detail 0: a click from the keyboard (Enter/Space), not the mouse.
    offerUndo(row, button, event instanceof MouseEvent && event.detail === 0);
  },
  { capture: true }
);

// Lowering the quantity to 0 (typing 0, or the minus button at 1) removes the line too, so it offers Undo as well.
document.addEventListener(
  ThemeEvents.quantitySelectorUpdate,
  (event) => {
    if (!(event.target instanceof Element) || event.detail?.quantity !== 0) return;
    const row = event.target.closest('cart-items-component .cart-items__table-row');
    if (row) offerUndo(row, event.target, row.contains(document.activeElement));
  },
  { capture: true }
);

document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof Element && event.target.matches('#cart-drawer dialog')) dismissUndo();
  },
  { capture: true }
);

/* Newsletter popup (sections/ns-email-popup.liquid). Waits the set delay, then opens unless it was closed in
   the last few days or this browser already signed up. A signup reloads the page with the result, which
   opens straight away. Stays out of the way if another dialog (cart, menu, search) is already open. */
const EMAIL_POPUP_KEY = 'ns-email-popup';
const emailPopup = document.querySelector('[data-ns-email-popup]');

const readPopupMemory = () => {
  try {
    return JSON.parse(localStorage.getItem(EMAIL_POPUP_KEY) ?? '{}');
  } catch {
    return {};
  }
};
const writePopupMemory = (value) => {
  try {
    localStorage.setItem(EMAIL_POPUP_KEY, JSON.stringify(value));
  } catch {}
};

if (emailPopup instanceof HTMLDialogElement) {
  const { state, delay, againDays } = emailPopup.dataset;
  // Shopify returns to the page with the posting form's id in the hash; other signup forms (footer) don't open this.
  const postedHere = window.location.hash === '#NsEmailPopupForm';
  const inEditor = Boolean(window.Shopify?.designMode);

  const open = () => {
    if (emailPopup.open || document.querySelector('dialog[open]')) return;
    emailPopup.showModal();
  };
  const close = () => {
    emailPopup.classList.add('is-closing');
    setTimeout(() => {
      emailPopup.classList.remove('is-closing');
      emailPopup.close();
    }, 200);
  };

  emailPopup.addEventListener('close', () => {
    const memory = readPopupMemory();
    if (!memory.subscribed && !inEditor) {
      writePopupMemory({ ...memory, hiddenUntil: Date.now() + Number(againDays || 3) * 86400000 });
    }
  });
  emailPopup.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target === emailPopup || event.target.closest('.ns-email-popup__close, .ns-email-popup__done')) close();
  });

  if (state === 'success') writePopupMemory({ subscribed: true });
  // An error from the footer form isn't this popup's to show.
  if (state === 'error' && !postedHere) {
    emailPopup.querySelector('.ns-email-popup__error')?.remove();
    emailPopup.querySelector('.ns-email-popup__input')?.removeAttribute('aria-invalid');
  }

  if (inEditor) {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target instanceof Element && event.target.contains(emailPopup)) open();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target instanceof Element && event.target.contains(emailPopup)) emailPopup.close();
    });
  } else if (state && postedHere) {
    open();
  } else {
    const memory = readPopupMemory();
    if (!memory.subscribed && !(memory.hiddenUntil > Date.now())) {
      setTimeout(open, Number(delay) || 0);
    }
  }
}

/* Collection sort button reads "Sort: Best selling" (snippets/sorting.liquid). Updated right away on change, before
   the filtered results come back. */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'sort_by' || !input.dataset.optionName) return;
  input
    .closest('sorting-filter-component')
    ?.querySelectorAll('.ns-sort-current')
    .forEach((current) => (current.textContent = `: ${input.dataset.optionName}`));
});

/* Collection header tiles that act as the "Product type" filter (sections/collection-banner.liquid). Filters
   update the grid in place and change the URL, so the tiles' highlight and links follow the URL: a tile
   switches to its type (like picking a category), or clears it when it's already the one showing. Other
   filters (size, in stock) carry over. Combining types is still possible in the sidebar. */
const TYPE_PARAM = 'filter.p.product_type';

function syncTypeTiles() {
  const tiles = document.querySelectorAll('[data-ns-type-tiles] [data-ns-type]');
  if (!tiles.length) return;
  const active = new URLSearchParams(window.location.search).getAll(TYPE_PARAM);

  tiles.forEach((tile) => {
    if (!(tile instanceof HTMLAnchorElement)) return;
    const type = tile.dataset.nsType ?? '';
    const isOn = active.includes(type);
    const params = new URLSearchParams(window.location.search);
    params.delete(TYPE_PARAM);
    params.delete('page');
    if (!isOn) params.append(TYPE_PARAM, type);

    const query = params.toString();
    tile.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
    tile.classList.toggle('is-current', isOn);
    if (isOn) tile.setAttribute('aria-current', 'true');
    else tile.removeAttribute('aria-current');
  });
}

syncTypeTiles();
let lastTypeSearch = window.location.search;
new MutationObserver(() => {
  if (window.location.search === lastTypeSearch) return;
  lastTypeSearch = window.location.search;
  syncTypeTiles();
}).observe(document.body, { childList: true, subtree: true });
window.addEventListener('popstate', syncTypeTiles);

/* Mobile quick add (snippets/ns-quick-size.liquid): the "+" on a card opens a bottom sheet to pick color and
   size, then Add to bag submits the card's product form. Handled on window in the capture phase and stopped
   there, so taps inside the sheet never reach the product card's "go to product" click. */
function selectSheetSize(sheet, button) {
  sheet.querySelectorAll('.ns-quick-sheet__size[aria-pressed="true"]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
  const form = sheet.closest('form');
  const add = sheet.querySelector('.ns-quick-sheet__add');
  const sizeValue = sheet.querySelector('.ns-quick-sheet__size-value');
  const price = sheet.querySelector('.ns-quick-sheet__price');

  if (button) {
    button.setAttribute('aria-pressed', 'true');
    if (form?.elements.id) form.elements.id.value = button.dataset.variantId;
  }
  if (sizeValue) sizeValue.textContent = button?.dataset.label ?? 'Select';
  if (price) price.textContent = button?.dataset.price ?? price.dataset.default ?? '';
  if (add) {
    add.disabled = !button;
    add.removeAttribute('data-added');
    setSheetLabel(add, button ? 'Add to bag' : 'Select a size');
  }
}

// The label sits in its own span so the checkmark markup beside it stays put.
const setSheetLabel = (add, text) => {
  const label = add.querySelector('.ns-quick-sheet__add-label');
  if (label) label.textContent = text;
  else add.textContent = text;
};

function openSheet(sheet) {
  const available = [...sheet.querySelectorAll('.ns-quick-sheet__sizes:not([hidden]) .ns-quick-sheet__size:not([disabled])')];
  // One size left (or one-size items): pick it so Add to bag is ready.
  selectSheetSize(sheet, available.length === 1 ? available[0] : null);
  sheet.showModal();
  document.documentElement.classList.add('ns-sheet-open');
}

const closeSheet = (sheet) => {
  sheet.classList.add('is-closing');
  setTimeout(() => {
    sheet.classList.remove('is-closing');
    sheet.close();
  }, 200);
};

window.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const open = event.target.closest('.ns-quick-add-open');
    if (open) {
      event.preventDefault();
      event.stopPropagation();
      const sheet = open.closest('form')?.querySelector('.ns-quick-sheet');
      if (sheet) openSheet(sheet);
      return;
    }

    const sheet = event.target.closest('.ns-quick-sheet');
    if (!sheet) return;
    event.stopPropagation();

    // Taps on the dimmed backdrop land on the dialog itself.
    if (event.target === sheet || event.target.closest('.ns-quick-sheet__close')) {
      closeSheet(sheet);
      return;
    }

    const row = event.target.closest('.ns-quick-sheet__row');
    if (row) {
      sheet.querySelectorAll('.ns-quick-sheet__row').forEach((b) => b.setAttribute('aria-pressed', String(b === row)));
      sheet.querySelectorAll('.ns-quick-sheet__sizes').forEach((group) => {
        group.hidden = group.dataset.row !== row.dataset.row;
      });
      const rowValue = sheet.querySelector('.ns-quick-sheet__row-value');
      if (rowValue) rowValue.textContent = row.dataset.label ?? '';
      const selected = sheet.querySelector('.ns-quick-sheet__size[aria-pressed="true"]')?.dataset.label;
      const match = [...sheet.querySelectorAll(`.ns-quick-sheet__sizes[data-row="${row.dataset.row}"] .ns-quick-sheet__size`)]
        .find((b) => b.dataset.label === selected && !b.disabled);
      selectSheetSize(sheet, match ?? null);
      return;
    }

    const size = event.target.closest('.ns-quick-sheet__size');
    if (size && !size.disabled) selectSheetSize(sheet, size);
  },
  { capture: true }
);

document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof Element && event.target.matches('.ns-quick-sheet')) {
      if (!document.querySelector('.ns-quick-sheet[open]')) document.documentElement.classList.remove('ns-sheet-open');
    }
  },
  { capture: true }
);

// How long the quick-add sheet stays open for the checkmark (its burst ends at about 0.9s).
const SHEET_CHECK_MS = 1000;

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const sheet =event.target instanceof Element ? event.target.closest('.ns-quick-size')?.querySelector('.ns-quick-sheet[open]') : null;
  if (!sheet) return;
  const add = sheet.querySelector('.ns-quick-sheet__add');
  // Play the product page's checkmark right away (not disabled: that would turn the button gray under it).
  if (add) {
    add.dataset.added = 'true';
    add.style.pointerEvents = 'none';
  }
  const started = performance.now();
  // No promise to wait on: don't leave the button unclickable.
  if (!event.promise) {
    setTimeout(() => {
      add?.removeAttribute('data-added');
      add?.style.removeProperty('pointer-events');
    }, SHEET_CHECK_MS);
    return;
  }
  event.promise
    .then(({ detail }) => {
      if (detail?.didError) throw new Error();
      // Let the checkmark finish, then the sheet steps aside and the cart drawer opens with the new item.
      setTimeout(() => {
        sheet.close();
        add?.style.removeProperty('pointer-events');
      }, Math.max(0, SHEET_CHECK_MS - (performance.now() - started)));
    })
    .catch(() => {
      if (add) {
        add.removeAttribute('data-added');
        add.style.removeProperty('pointer-events');
        setSheetLabel(add, "Couldn't add, try again");
      }
    });
});

/* Shopify's reason for a refused add ("You can't add more ... to the cart"), kept per card bar for the label below.
   It arrives just before the add's promise settles. Network errors keep the generic "try again". */
const quickAddErrors = new WeakMap();
document.addEventListener(StandardEvents.cartError, (event) => {
  const root = event.target instanceof Element ? event.target.closest('.ns-quick-size') : null;
  if (root && event.code === 'INVALID' && typeof event.error === 'string') quickAddErrors.set(root, event.error);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const root = event.target instanceof HTMLElement ? event.target.closest('.ns-quick-size') : null;
  if (!(root instanceof HTMLElement) || root.querySelector('.ns-quick-sheet[open]')) return;

  const label = root.querySelector('.ns-quick-size__label');
  const button = root.querySelector('.is-adding');
  const liveRegion = root.querySelector('[ref="liveRegion"]');
  const original = label?.dataset.original ?? label?.textContent ?? '';
  if (label) label.dataset.original = original;
  root.dataset.busy = 'true';
  quickAddErrors.delete(root);

  const finish = (text, failed) => {
    delete root.dataset.busy;
    if (label) {
      label.textContent = text;
      label.dataset.status = failed ? 'error' : 'added';
    }
    if (liveRegion) liveRegion.textContent = text;
    // A failed size shouldn't stay filled in like a successful one.
    if (failed) button?.classList.remove('is-adding');
    root.classList.add('is-added');
    setTimeout(() => {
      button?.classList.remove('is-adding');
      root.classList.remove('is-added');
      if (label) {
        label.textContent = original;
        delete label.dataset.status;
      }
      if (liveRegion) liveRegion.textContent = '';
    }, failed ? ERROR_MS : CONFIRM_MS);
  };
  const fail = () => finish(quickAddErrors.get(root) || "Couldn't add, try again", true);

  const color = button?.closest('.ns-quick-size__row')?.querySelector('.ns-quick-size__row-label')?.textContent.trim();
  const added = [color, button?.textContent.trim()].filter(Boolean).join(' ');

  if (!event.promise) {
    delete root.dataset.busy;
    return;
  }
  event.promise.then(({ detail }) => (detail?.didError ? fail() : finish(`Added ${added} ✓`, false))).catch(fail);
});

/* Cart quantity boxes (drawer and /cart): Enter applies the typed quantity. The boxes belong to the cart form, whose
   first submit button is Check out, so the browser's default would go straight to checkout. Horizon updates the
   quantity on blur, so blur, then hand focus back. */
document.addEventListener('keydown', (event) => {
  const input = event.target;
  if (event.key !== 'Enter' || !(input instanceof HTMLInputElement) || input.type !== 'number') return;
  if (!input.closest('cart-items-component')) return;
  event.preventDefault();
  input.blur();
  input.focus();
});

/* Collection and search filters. Horizon changes the URL as filters change but never redraws the products on
   Back/Forward, and gives no sign while results load or when they fail to load. */
const resultsKey = (search) => {
  const params = new URLSearchParams(search);
  params.delete('page');
  params.sort();
  return params.toString();
};
let shownResults = resultsKey(window.location.search);


const RESULTS_LOADING_DELAY_MS = 250;
const RESULTS_STALLED_MS = 12000;

/** @param {Element | null} section @param {Promise<unknown>} promise */
function trackResults(section, promise) {
  const list = section?.querySelector('results-list');
  if (!(list instanceof HTMLElement)) return;
  list.querySelector('.ns-results-error')?.remove();
  let settled = false;
  const loading = setTimeout(() => !settled && list.classList.add('ns-results-loading'), RESULTS_LOADING_DELAY_MS);
  const done = () => {
    settled = true;
    clearTimeout(loading);
    clearTimeout(stalled);
    list.classList.remove('ns-results-loading');
  };
  const showError = () => {
    done();
    const current = section?.querySelector('results-list');
    if (!(current instanceof HTMLElement) || current.querySelector('.ns-results-error')) return;
    const error = document.createElement('div');
    error.className = 'ns-results-error';
    error.setAttribute('role', 'alert');
    error.innerHTML =
      '<p>We couldn’t update the products. Check your connection and try again.</p>' +
      '<button type="button" class="ns-results-error__retry">Try again</button>';
    // The URL already holds the new filters, so a reload shows the right products.
    error.querySelector('button')?.addEventListener('click', () => window.location.reload());
    current.prepend(error);
  };
  // A view transition can swallow a failed render, so a request that never finishes counts as failed too.
  const stalled = setTimeout(() => !settled && showError(), RESULTS_STALLED_MS);
  promise.then(done, (error) => (error?.name === 'AbortError' ? done() : showError()));
}

/** @param {Event & { promise?: Promise<unknown> }} event */
function onResultsUpdate(event) {
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  // Predictive search in the header sends the same event; only the page's own filters count.
  if (!section || !section.querySelector('facets-form-component') || !section.querySelector('results-list')) return;
  shownResults = resultsKey(window.location.search);
  if (event.promise) trackResults(section, event.promise);
}
document.addEventListener(StandardEvents.collectionUpdate, onResultsUpdate);
document.addEventListener(StandardEvents.searchUpdate, onResultsUpdate);

window.addEventListener('popstate', () => {
  const key = resultsKey(window.location.search);
  if (key === shownResults) return;
  const form = document.querySelector('facets-form-component[section-id]');
  const sectionId = form?.getAttribute('section-id');
  if (!form || !sectionId) return;
  shownResults = key;
  const section = form.closest('.shopify-section');
  const render = sectionRenderer.renderSection(sectionId);
  trackResults(section, render);
  render.then(syncTypeTiles, () => {});
});

/* Collection and search addresses that would show the wrong thing. */
{
  const params = new URLSearchParams(window.location.search);

  // A reversed price range (from $50 up to $10) finds nothing and showed a "$50.00–$10.00" pill: swap the two.
  const min = params.get('filter.v.price.gte');
  const max = params.get('filter.v.price.lte');
  if (min && max && Number(min) > Number(max) && !Number.isNaN(Number(min)) && !Number.isNaN(Number(max))) {
    params.set('filter.v.price.gte', max);
    params.set('filter.v.price.lte', min);
    window.location.replace(`${window.location.pathname}?${params}${window.location.hash}`);
  }

  // "Go to page 1" on a page past the end (snippets/product-grid.liquid) keeps the filters and sort.
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-ns-page-one]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    link.href = url.toString();
  });

  // Page 2 and on open at the products, not above the banner and type tiles again. Only on a fresh visit:
  // Back and reload keep the browser's own scroll position.
  const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (performance.getEntriesByType('navigation')[0]);
  if (Number(params.get('page')) > 1 && navigation?.type === 'navigate' && !window.location.hash) {
    const toResults = () => {
      const wrapper = document.querySelector('.collection-wrapper');
      if (!wrapper) return;
      const scroller =
        window.matchMedia('(min-width: 990px)').matches && document.querySelector('.page-wrapper')
          ? document.querySelector('.page-wrapper')
          : document.scrollingElement;
      if (!scroller) return;
      const header = document.querySelector('header-component, #header-component, .header');
      const stickyHeader = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position);
      const offset = (stickyHeader ? header.getBoundingClientRect().height : 0) + 8;
      const top = wrapper.getBoundingClientRect().top - (scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top);
      scroller.scrollTo({ top: scroller.scrollTop + top - offset, behavior: 'instant' });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', toResults, { once: true });
    else toResults();
  }
}
